'use client';

import React from 'react';
import { useReducedMotion } from 'framer-motion';
import { Area, AreaChart, ResponsiveContainer } from 'recharts';

import { cn } from '@/lib/utils';

import ChartTexture, { textureFill, textureMask, textureWash } from './ChartTexture';
import { PRIMARY } from './palette';

export interface SparklineProps {
  /** Plain numbers, oldest first. */
  data: number[];
  color?: string;
  height?: number;
  textured?: boolean;
  /** Describes the trend for screen readers, e.g. "Spend, up 12% this week". */
  ariaLabel: string;
  className?: string;
}

/**
 * Chrome-free trend for stat tiles: no axes, no grid, no tooltip. The tile's
 * value and delta carry the numbers; the sparkline only carries the shape.
 */
export default function Sparkline({
  data,
  color = PRIMARY,
  height = 44,
  textured = true,
  ariaLabel,
  className,
}: SparklineProps) {
  const reactId = React.useId();
  const id = reactId.replace(/:/g, '');
  const reduceMotion = useReducedMotion();
  const series = React.useMemo(
    () => data.map((value, index) => ({ index, value })),
    [data]
  );

  return (
    <div
      className={cn('w-full', className)}
      style={{ height }}
      role="img"
      aria-label={ariaLabel}
    >
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={series} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
          <defs>
            <ChartTexture id={id} color={color} spacing={4} radius={0.75} />
          </defs>
          <Area
            type="monotone"
            dataKey="value"
            stroke="none"
            fill={textureWash(id)}
            isAnimationActive={!reduceMotion}
          />
          <Area
            type="monotone"
            dataKey="value"
            stroke={color}
            strokeWidth={1.5}
            fill={textured ? textureFill(id) : textureWash(id)}
            mask={textured ? textureMask(id) : undefined}
            isAnimationActive={!reduceMotion}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
